// Description: Page to add a new SMS channel
// Updated: 28/08/2025
// Author: Vitor de Jesus

// React
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Material UI Components
import {
  Button,
  Box,
  Typography,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

// Components and functions for the project
import StepIndicator from "../../components/DynamicForm/StepIndicator";
import StatusIcon from "../../components/StatusLabel/StatusIcon";
import { notify } from "../../utils/utils";
import { ChannelAddSMS } from "../../api/aws/Channels/ChannelAddSMS";
import CampaignModal from "../../components/Modals/CampaignModal";
import themeColors from "../../components/ThemeColors/ThemeColors";
import { isAuthenticated } from "../../authMiddleware";
import use123zapStore from "../../context/123zapStore";
import ReturnToLastPage from "../../components/ChannelAdd/ReturnToLastPage";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

// Render The Page
function ChannelPageAddSMS() {
  let Navigate = useNavigate();

  const [firstTime, setFirstTime] = useState(30);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const creatingCampaignWithHelpMode =
    JSON.parse(sessionStorage.getItem("creating_campaign")) || null;
  const [openModalCampaign, setOpenModalCampaign] = useState(false);
  const HandleChannels = () => {
    creatingCampaignWithHelpMode === true
      ? Navigate("/campanhas/adicionar")
      : Navigate("/canais");
  };
  const userAuthenticated = use123zapStore((state) => state.userAuthenticated);
  const setAlreadyFetchChannels = use123zapStore(
    (state) => state.setAlreadyFetchChannels
  );
  const userInfo = use123zapStore((state) => state.userInfo);
  const setUserInfo = use123zapStore((state) => state.setUserInfo);
  const planoAtual = use123zapStore((state) => state.planoAtual);

  useEffect(() => {
    if (userAuthenticated && Object.keys(userInfo).length === 0) {
      const { decryptedData } = isAuthenticated();
      setUserInfo(decryptedData);
    }
  }, [userAuthenticated]);

  useEffect(() => {
    if (planoAtual !== "UltraZap") {
      Navigate("/acesso-restrito");
    }
  }, [planoAtual]);

  const handleAdicionar = async () => {
    try {
      setFirstTime(30);

      const addSMS = await ChannelAddSMS({ confirm: "solicitar" });

      if (addSMS.message === "success") {
        setSuccess(true);
        setAlreadyFetchChannels(false);
      }
    } catch (error) {
      console.error("Error connecting to instance:", error);
    } finally {
      setFirstTime(0);
    }
  };

  const HandleWhatsApp = () => {
    window.open(
      "https://wa.me/553121300297?text=Ol%C3%A1%2C%20estou%20precisando%20de%20suporte!%20",
      "_blank"
    );
  };

  const steps = [
    {
      stepNumber: 1,
      title: "Vamos solicitar seu canal SMS?",
      description: (
        <span>
          Antes de começar, precisamos compartilhar uma informação importante.{" "}
          <br />
          Em nossa plataforma, os envios de SMS são realizados por meio de um
          <strong style={{ color: "#8142FC" }}>
            {" "}
            número curto compartilhado{" "}
          </strong>
          . <br />
          <hr style={{ margin: "10px 0", border: "0.1px solid #F9F9F9" }} />
          Um número curto é um código numérico menor que um número de telefone
          convencional, utilizado para o envio de mensagens em larga escala,
          garantindo maior agilidade e entregabilidade. <br />
          <hr style={{ margin: "10px 0", border: "0.1px solid #F9F9F9" }} />O
          termo compartilhado significa que esse mesmo número pode ser utilizado
          por diferentes empresas para o envio de SMS, o que possibilita reduzir
          custos e simplificar a operação.
        </span>
      ),
    },
    {
      stepNumber: 2,
      title: "O que é um canal compartilhado?",
      description: (
        <span>
          Chegou a hora de conectar o seu WhatsApp. Para isso, abra o aplicativo
          WhatsApp no seu celular{" "}
          <strong style={{ color: "#8142FC" }}> {" > "}</strong> Dispositivos
          Conectados <strong style={{ color: "#8142FC" }}> {" > "}</strong>{" "}
          Conectar um dispositivo{" "}
          <strong style={{ color: "#8142FC" }}> {" > "}</strong> Escanei o QR
          Code abaixo.
        </span>
      ),
    },
    {
      stepNumber: 3,
      title: success ? "SMS adicionado com sucesso!" : "Ops! Algo deu errado",
      description: success ? (
        <span>
          Agora você pode começar a enviar SMS para seus clientes. Clique no
          botão abaixo para verificar seus canais.
        </span>
      ) : (
        <span>
          Parece que houve um problema ao adicionar o seu SMS. Por favor, tente
          novamente mais tarde ou entre em contato com o suporte para obter
          ajuda.
        </span>
      ),
    },
  ];

  const canMoveForward = () => {
    return true;
  };

  useEffect(() => {
    if (firstTime > 0 && currentStep === 2) {
      handleAdicionar();
    }
    if (firstTime === 0 && currentStep === 2) {
      setCurrentStep(3);
    }
  }, [firstTime, currentStep]);

  const FormContent = ({ currentStep, steps, openModal }) => {
    if (currentStep === 1) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>
        </Box>
      );
    } else if (currentStep === 2) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            Aguarde um instante! 😉
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            Estamos solicitando e configurando o seu canal SMS. Este processo
            pode levar alguns instantes. Por favor, aguarde.
          </Typography>

          <Box
            sx={{
              border: "2px solid #F5F3FF",
              borderRadius: "50%", // Garante que seja um círculo
              height: "200px", // Define o tamanho fixo para o círculo
              width: "200px", // Define a mesma largura para o círculo
              display: "flex",
              flexDirection: "column", // Organiza os itens em coluna
              justifyContent: "center", // Centraliza verticalmente
              alignItems: "center", // Centraliza horizontalmente
              textAlign: "center", // Alinhamento do texto centralizado
            }}
          >
            <CircularProgress
              sx={{
                position: "absolute", // Faz com que o progresso circular fique em cima do Box
                color: "#F5F3FF", // Cor do progresso circular
              }}
              size={200} // Tamanho do círculo
              thickness={1} // Espessura do progresso
            />
          </Box>
        </Box>
      );
    } else if (currentStep === 3) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
          </Box>

          {success ? (
            <Button
              variant="outlined"
              sx={{
                padding: "0 12px",
                minWidth: {
                  xs: "100%",
                  sm: "0px",
                },
                height: "36px",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "9999px",
                fontSize: "16px",
                fontWeight: 500,
              }}
              onClick={openModal}
              startIcon={<CheckCircleOutlineIcon sx={{ color: "#000000ff" }} />}
            >
              Verificar meus canais
            </Button>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: "10px", sm: "10px" },
                marginTop: { xs: "30px", sm: "20px" },
                flexDirection: {
                  xs: "column", // Empilha os botões em telas menores
                  sm: "row", // Alinha os botões na horizontal em telas maiores
                },
                width: "100%", // Faz os botões ocuparem toda a largura
              }}
            >
              <Button
                variant="contained"
                sx={{
                  padding: { xs: "16px 20px", sm: "10px 25px" },
                  width: "100%", // Faz o botão ocupar toda a largura
                  maxWidth: "400px", // Define uma largura máxima opcional
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "6px",
                  fontSize: "15px",
                }}
                onClick={HandleWhatsApp}
                startIcon={<WhatsAppIcon sx={{ color: "white" }} />}
              >
                Falar com o suporte
              </Button>
              <Button
                variant="contained"
                sx={{
                  padding: { xs: "16px 20px", sm: "10px 25px" },
                  width: "100%", // Faz o botão ocupar toda a largura
                  maxWidth: "400px", // Define uma largura máxima opcional
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "6px",
                  fontSize: "15px",
                  backgroundColor: "#300D68",
                }}
                onClick={() => {
                  Navigate(0);
                }}
              >
                Tentar novamente
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    return null;
  };

  const errorNotification = (message) => {
    setError(true);

    setTimeout(() => {
      setError(false);
    }, 5000);

    notify("Error", message, true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: "100vh", sm: "100vh" },
        backgroundColor: { xs: "#fff", sm: "#F1F5F9" },
      }}
    >
      <ReturnToLastPage />
      <Box
        sx={{
          minWidth: "100%",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          opacity: 0.8,
          flex: 1,
          gap: "15px",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            id="form"
            sx={{
              background: "#FFF",
              minHeight: {
                xs: "100vh", // Ocupa a altura inteira em telas pequenas
                sm: "100%", // Altura personalizada em telas maiores
                md: "583.2px",
                lg: "583.2px",
                xl: "583.2px",
              },
              minWidth: {
                xs: "100%", // Ocupa a largura inteira em telas pequenas
                sm: "100%", // Largura personalizada em telas maiores
                md: "736.2px",
                lg: "736.2px",
                xl: "736.2px",
              },
              maxWidth: {
                xs: "100%", // Ocupa a largura inteira em telas pequenas
                sm: "100%", // Largura personalizada em telas maiores
                md: "736.2px",
                lg: "736.2px",
                xl: "736.2px",
              },
              padding: "30px",
              borderRadius: {
                xs: "0", // Sem borda arredondada em telas pequenas
                sm: ".5rem", // Bordas arredondadas em telas médias ou maiores
              },
              boxShadow: {
                xs: "none", // Sem sombra em telas pequenas
                sm: "0 2px 4px -1px rgba(0, 0, 0, 0.1)", // Sombra em telas maiores
              },
              display: "flex",
              flexDirection: "column",
              justifyContent: {
                xs: "flex-start",
                sm: "flex-start",
                md: "space-between",
                lg: "space-between",
              },
              height: "100%", // Faz com que o form ocupe toda a altura disponível
            }}
          >
            <FormContent
              currentStep={currentStep}
              steps={steps}
              openModal={() =>
                creatingCampaignWithHelpMode === true
                  ? setOpenModalCampaign(true)
                  : Navigate("/canais")
              }
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: "0px", sm: "10px" },
                marginTop: { xs: "30px", sm: "20px" },
                flexDirection: {
                  xs: "column", // Empilha os botões em telas menores
                  sm: "row", // Alinha os botões na horizontal em telas maiores
                },
              }}
            >
              <Tooltip
                title={
                  currentStep === 2 ? (
                    <Typography variant="body2">
                      Não avance antes de escanear o QR Code em seu WhatsApp
                    </Typography>
                  ) : null
                }
                arrow
                placement="top"
              >
                <Button
                  variant="contained"
                  sx={{
                    padding: "0 12px",
                    minWidth: {
                      xs: "100%",
                      sm: "0", // Largura mínima em telas maiores
                    },
                    height: "36px",
                    display:
                      currentStep === steps.length
                        ? "none"
                        : firstTime > 0 && currentStep === 2
                        ? "none"
                        : "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "9999px",
                    fontSize: "12px",
                    fontWeight: 500,
                    marginLeft: "auto",
                    marginTop: { xs: "20px", sm: "0" },
                  }}
                  onClick={() => {
                    if (canMoveForward()) {
                      setCurrentStep((prevStep) => prevStep + 1);
                    } else {
                      errorNotification("Preencha o nome do WhatsApp");
                    }
                  }}
                >
                  Solicitar
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
      <CampaignModal
        cancelText="Adicionar mais um canal"
        confirmText="Retornar ao formulário"
        subTitle={
          "Percebemos que você está configurando uma campanha. Gostaria de retornar ao formulário?"
        }
        isOpen={openModalCampaign}
        onClose={() => setOpenModalCampaign(false)}
        onCancel={() => Navigate("/canais/adicionar")}
        onConfirm={() => (
          HandleChannels(), sessionStorage.setItem("ReturToCampaign", true)
        )}
        title={"Canal adicionado com sucesso"}
        iconColor={themeColors.purple400}
      />
    </Box>
  );
}

export default ChannelPageAddSMS;
